import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import {
  Add,
  Check,
  Close,
  Delete,
  Edit,
  Phone as PhoneIcon,
  Sms,
  Star,
  StarBorder,
} from "@mui/icons-material"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  PhoneNumber,
  useCreatePhoneNumberMutation,
  useDeletePhoneNumberMutation,
  useUpdatePhoneNumberMutation,
} from "../../apis/appApi"

const LABELS = ["HOME", "WORK", "MOBILE", "OTHER"] as const

type PhoneNumberSectionProps = {
  clientId: number
  phoneNumbers: PhoneNumber[]
  onSuccess: (message: string) => void
}

type EditState = {
  id: number | null // null = new
  number: string
  label: string
}

const labelI18nKey: Record<string, string> = {
  HOME: "clients.phone.home",
  WORK: "clients.phone.work",
  MOBILE: "clients.phone.mobile",
  OTHER: "clients.phone.other",
}

export const PhoneNumberSection: FC<PhoneNumberSectionProps> = ({
  clientId,
  phoneNumbers,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [createPhone, { isLoading: isCreating }] = useCreatePhoneNumberMutation()
  const [updatePhone, { isLoading: isUpdating }] = useUpdatePhoneNumberMutation()
  const [deletePhone] = useDeletePhoneNumberMutation()

  const [editState, setEditState] = useState<EditState | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PhoneNumber | null>(null)

  const isSaving = isCreating || isUpdating

  const handleStartAdd = useCallback(() => {
    setEditState({ id: null, number: "", label: "MOBILE" })
  }, [])

  const handleStartEdit = useCallback((phone: PhoneNumber) => {
    setEditState({ id: phone.id, number: phone.number, label: phone.label || "" })
  }, [])

  const handleCancel = useCallback(() => {
    setEditState(null)
  }, [])

  const handleSave = useCallback(async () => {
    if (!editState) return
    const trimmedNumber = editState.number.trim()
    if (!trimmedNumber) return

    try {
      if (editState.id === null) {
        await createPhone({
          clientId,
          number: trimmedNumber,
          label: editState.label || null,
        }).unwrap()
        onSuccess(t("clients.phone.added"))
      } else {
        await updatePhone({
          clientId,
          id: editState.id,
          number: trimmedNumber,
          label: editState.label || null,
        }).unwrap()
        onSuccess(t("clients.phone.updated"))
      }
      setEditState(null)
    } catch {
      // Error handled by RTK Query
    }
  }, [editState, clientId, createPhone, updatePhone, onSuccess, t])

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    try {
      await deletePhone({ clientId, id: deleteTarget.id }).unwrap()
      onSuccess(t("clients.phone.deleted"))
      setDeleteTarget(null)
    } catch {
      // Error handled by RTK Query
    }
  }, [deleteTarget, clientId, deletePhone, onSuccess, t])

  const isNumberValid = editState ? editState.number.trim().length > 0 : false

  return (
    <Paper {...uiSurface("UI-DLV2-086")} variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ color: "text.secondary", display: "flex" }}>
          <PhoneIcon fontSize="small" />
        </Box>
        <Typography variant="subtitle2" fontWeight={600}>
          {t("clients.detail.phoneNumbers")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({phoneNumbers.length})
        </Typography>
      </Stack>

      <Stack spacing={1}>
        {phoneNumbers.map((phone, index) => {
          const isEditing = editState?.id === phone.id
          const isPrimary = index === 0

          if (isEditing) {
            return (
              <Stack
                key={phone.id}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <TextField
                  size="small"
                  value={editState.number}
                  onChange={(e) =>
                    setEditState((s) => (s ? { ...s, number: e.target.value } : s))
                  }
                  placeholder={t("clients.phone.number")}
                  sx={{ flex: 1 }}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && isNumberValid) handleSave()
                    if (e.key === "Escape") handleCancel()
                  }}
                />
                <TextField
                  select
                  size="small"
                  value={editState.label}
                  onChange={(e) =>
                    setEditState((s) => (s ? { ...s, label: e.target.value } : s))
                  }
                  sx={{ minWidth: 110 }}
                >
                  <MenuItem value="">{"\u2014"}</MenuItem>
                  {LABELS.map((l) => (
                    <MenuItem key={l} value={l}>
                      {t(labelI18nKey[l])}
                    </MenuItem>
                  ))}
                </TextField>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleSave}
                  disabled={!isNumberValid || isSaving}
                >
                  {isSaving ? <CircularProgress size={18} /> : <Check fontSize="small" />}
                </IconButton>
                <IconButton size="small" onClick={handleCancel} disabled={isSaving}>
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
            )
          }

          return (
            <Stack
              key={phone.id}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                py: 0.5,
                "&:hover .phone-actions": { opacity: 1 },
              }}
            >
              {/* F088: Primary phone star indicator */}
              <Tooltip
                title={isPrimary ? t("clients.phone.primary") : t("clients.phone.primaryHint")}
              >
                <Box sx={{ display: "flex", color: isPrimary ? "warning.main" : "action.disabled" }}>
                  {isPrimary ? (
                    <Star sx={{ fontSize: 18 }} />
                  ) : (
                    <StarBorder sx={{ fontSize: 18 }} />
                  )}
                </Box>
              </Tooltip>

              <Typography variant="body2" sx={{ flex: 1 }}>
                {phone.number}
              </Typography>
              {phone.label && (
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>
                  {t(labelI18nKey[phone.label] || phone.label)}
                </Typography>
              )}

              {/* F090: SMS consent indicator (read-only placeholder — no DB column yet) */}
              <Tooltip title={t("clients.phone.smsConsentHint")}>
                <Box sx={{ display: "flex", color: "action.disabled" }}>
                  <Sms sx={{ fontSize: 16 }} />
                </Box>
              </Tooltip>

              <Stack
                className="phone-actions"
                direction="row"
                spacing={0}
                sx={{ opacity: 0, transition: "opacity 0.15s" }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleStartEdit(phone)}
                  disabled={editState !== null}
                >
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeleteTarget(phone)}
                  disabled={editState !== null}
                >
                  <Delete sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            </Stack>
          )
        })}

        {/* Add new phone number inline form */}
        {editState?.id === null && (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              value={editState.number}
              onChange={(e) =>
                setEditState((s) => (s ? { ...s, number: e.target.value } : s))
              }
              placeholder={t("clients.phone.number")}
              sx={{ flex: 1 }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && isNumberValid) handleSave()
                if (e.key === "Escape") handleCancel()
              }}
            />
            <TextField
              select
              size="small"
              value={editState.label}
              onChange={(e) =>
                setEditState((s) => (s ? { ...s, label: e.target.value } : s))
              }
              sx={{ minWidth: 110 }}
            >
              <MenuItem value="">{"\u2014"}</MenuItem>
              {LABELS.map((l) => (
                <MenuItem key={l} value={l}>
                  {t(labelI18nKey[l])}
                </MenuItem>
              ))}
            </TextField>
            <IconButton
              size="small"
              color="primary"
              onClick={handleSave}
              disabled={!isNumberValid || isSaving}
            >
              {isSaving ? <CircularProgress size={18} /> : <Check fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={handleCancel} disabled={isSaving}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        )}

        {phoneNumbers.length === 0 && editState === null && (
          <Typography variant="body2" color="text.disabled">
            {t("clients.phone.noPhoneNumbers")}
          </Typography>
        )}
      </Stack>

      {editState === null && (
        <Button
          size="small"
          startIcon={<Add />}
          onClick={handleStartAdd}
          sx={{ mt: 1, textTransform: "none" }}
        >
          {t("clients.phone.add")}
        </Button>
      )}

      {/* Delete confirmation */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-087") }}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
      >
        <DialogTitle>{t("clients.phone.deleteConfirm")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deleteTarget?.number}
            {deleteTarget?.label ? ` (${t(labelI18nKey[deleteTarget.label] || deleteTarget.label)})` : ""}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t("cancel")}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}
